import React, { useState, useEffect } from "react";
import useSWR from 'swr';
import api from "../utils/api";
import { Bell, Calendar, User, Package, Zap, DollarSign, UserCheck, FileSignature, XCircle, RotateCcw, MessageSquare } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

const NotificationBell = ({ onViewRequests }) => {
  const [open, setOpen] = useState(false);
  const [viewedActivities, setViewedActivities] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem('viewedNotifications');
    if (stored) {
      try {
        return new Set(JSON.parse(stored));
      } catch (e) {
        return new Set();
      }
    }
    return new Set();
  });
  
  // SWR fetcher
  const fetcher = (url) => api.get(url).then(res => res.data);
  
  // Fetch recent activities with SWR (auto-refresh every 60s)
  const { data, error, isLoading } = useSWR(
    '/activity-log?limit=20',
    fetcher,
    {
      refreshInterval: 60000, // Auto-refresh every 60 seconds (was 30s)
      revalidateOnFocus: true, // Refresh when tab becomes active
      dedupingInterval: 10000 // Prevent duplicate requests within 10s
    }
  );
  
  const allActivities = data?.activities || [];
  
  // Filter to notification-worthy events
  const notificationEvents = allActivities.filter(activity => {
    const action = activity.action?.toLowerCase() || '';
    const resourceType = activity.resourceType?.toLowerCase() || '';
    
    // Notification-worthy events - comprehensive list
    // Note: Actions can be 'created' or 'CREATE' depending on source
    return (
      // Bookings
      (action === 'created' && resourceType === 'booking') ||
      (action === 'create' && resourceType === 'booking') ||
      (action === 'confirmed' && resourceType === 'booking') ||
      (action === 'cancelled' && resourceType === 'booking') ||
      (action === 'cancel' && resourceType === 'booking') ||
      (action === 'rescheduled' && resourceType === 'booking') ||
      (action === 'check_in' && resourceType === 'booking') ||
      (action === 'checked_in' && resourceType === 'booking') ||
      
      // Payments
      (action === 'payment_received' && resourceType === 'booking') ||
      (action === 'refunded' && resourceType === 'booking') ||
      (action === 'partially_refunded' && resourceType === 'booking') ||
      
      // Leads & Custom Requests
      (action === 'captured' && resourceType === 'lead') ||
      (action === 'created' && resourceType === 'lead') ||
      (action === 'create' && resourceType === 'lead') ||
      (action === 'created' && resourceType === 'custom_request') ||
      (action === 'create' && resourceType === 'custom_request') ||
      (action === 'submitted' && resourceType === 'custom_request') ||
      
      // Check-ins & Waivers
      (action === 'signed' && resourceType === 'waiver') ||
      
      // Services
      (action === 'created' && resourceType === 'service') ||
      (action === 'create' && resourceType === 'service')
    );
  });
  
  // Show max 10 in dropdown
  const recentNotifications = notificationEvents.slice(0, 10);
  
  // Count only UNVIEWED
  const unviewedCount = notificationEvents.filter(activity => {
    const id = activity.id || activity.timestamp;
    return id && !viewedActivities.has(id);
  }).length;
  
  // Cap badge at 9+
  const badgeCount = unviewedCount > 9 ? '9+' : unviewedCount;
  
  // Mark all as viewed when opened
  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    
    if (isOpen && recentNotifications.length > 0) {
      // Mark all current notifications as viewed
      const currentIds = recentNotifications.map(a => a.id || a.timestamp).filter(Boolean);
      const newViewed = new Set([...viewedActivities, ...currentIds]);
      setViewedActivities(newViewed);
      localStorage.setItem('viewedNotifications', JSON.stringify([...newViewed]));
    }
  };

  const getActivityIcon = (activity) => {
    const resource = activity.resourceType?.toLowerCase() || '';
    const action = activity.action?.toLowerCase() || '';
    
    // Payment/refund icons
    if (action === 'payment_received') return DollarSign;
    if (action === 'refunded' || action === 'partially_refunded') return RotateCcw;
    
    // Cancellation
    if (action === 'cancelled') return XCircle;
    
    // Custom Requests (high priority!)
    if (resource === 'custom_request') return MessageSquare;
    
    // Leads
    if (action === 'captured' && resource === 'lead') return UserCheck;
    if (action === 'created' && resource === 'lead') return UserCheck;
    
    // Check-in
    if (action === 'checked_in') return UserCheck;
    
    // Waiver
    if (action === 'signed' && resource === 'waiver') return FileSignature;
    
    // Bookings
    if (resource.includes('booking')) return Calendar;
    
    // Services
    if (resource.includes('service')) return Zap;
    if (resource.includes('asset')) return Package;
    if (resource.includes('team')) return User;
    
    return Calendar;
  };
  
  const getActivityColor = (activity) => {
    const action = activity.action?.toLowerCase() || '';
    const resource = activity.resourceType?.toLowerCase() || '';
    
    // Refunds/cancellations = red
    if (action === 'cancelled' || action === 'refunded' || action === 'partially_refunded') {
      return 'text-red-600';
    }
    // Payments = green
    if (action === 'payment_received') {
      return 'text-green-600';
    }
    // Custom requests = purple (high priority!)
    if (resource === 'custom_request') {
      return 'text-purple-600';
    }
    // Leads = orange
    if (action === 'captured' || (action === 'created' && activity.resourceType?.toLowerCase() === 'lead')) {
      return 'text-orange-600';
    }
    // Default = blue
    return 'text-blue-600';
  };

  const getActivityMessage = (activity) => {
    const action = activity.action?.toLowerCase() || '';
    const resource = activity.resourceType?.toLowerCase() || '';
    const name = activity.resourceName || activity.description?.substring(0, 30) || 'Item';
    
    // Custom messages for each event type
    if ((action === 'created' || action === 'create') && resource === 'booking') {
      return `New booking: ${name}`;
    }
    if (action === 'confirmed' && resource === 'booking') {
      return `Booking confirmed: ${name}`;
    }
    if ((action === 'cancelled' || action === 'cancel') && resource === 'booking') {
      return `Booking cancelled: ${name}`;
    }
    if (action === 'rescheduled' && resource === 'booking') {
      return `Booking rescheduled: ${name}`;
    }
    if (action === 'payment_received') {
      const amount = activity.details?.amount ? `$${activity.details.amount}` : '';
      return `Payment received ${amount} - ${name}`;
    }
    if (action === 'refunded') {
      const amount = activity.details?.amount ? `$${activity.details.amount}` : '';
      return `Refund issued ${amount} - ${name}`;
    }
    if (action === 'partially_refunded') {
      const amount = activity.details?.amount ? `$${activity.details.amount}` : '';
      return `Partial refund ${amount} - ${name}`;
    }
    // Custom Requests (high priority!)
    if (resource === 'custom_request') {
      return `📩 Custom request: ${name}`;
    }
    if ((action === 'captured' || action === 'created' || action === 'create') && resource === 'lead') {
      return `New lead: ${name}`;
    }
    if (action === 'checked_in' || action === 'check_in') {
      return `${name} checked in`;
    }
    if (action === 'signed' && resource === 'waiver') {
      return `Waiver signed by ${name}`;
    }
    if ((action === 'created' || action === 'create') && resource === 'service') {
      return `New service: ${name}`;
    }
    
    // Fallback
    return `${action} ${resource}: ${name}`;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unviewedCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {badgeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg">Notifications</h3>
            {unviewedCount > 0 && (
              <Badge variant="secondary">{unviewedCount} new</Badge>
            )}
          </div>
          
          {recentNotifications.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No recent notifications
            </p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {recentNotifications.map((activity, index) => {
                const ResourceIcon = getActivityIcon(activity);
                const iconColor = getActivityColor(activity);
                
                return (
                  <div 
                    key={activity.id || index} 
                    className="p-3 border rounded-lg hover:bg-accent transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <ResourceIcon className={`w-4 h-4 ${iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          {getActivityMessage(activity)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTimestamp(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
